import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  PlusIcon,
  PlayIcon,
  DocumentDuplicateIcon,
  ChartBarIcon,
  UserGroupIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentListIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { Button, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui';
import { AthleteRollupTable } from '@/components/evaluation';
import { PerTemplateAthleteTable } from '@/components/dashboard/PerTemplateAthleteTable';
import { CommonWeaknessCard } from '@/components/dashboard/CommonWeaknessCard';
import { ChartTooltip } from '@/components/dashboard/ChartTooltip';
import { TemplateEffectivenessCard } from '@/components/dashboard/TemplateEffectivenessCard';
import { SharedEvalsInboxCard } from '@/components/dashboard/SharedEvalsInboxCard';
import { ScoreRing } from '@/components/ui/ScoreRing';
import { useTemplates, useMyEvaluations } from '@/hooks';
import { useMyChannels } from '@/hooks/useChannels';
import { useCoachAthleteEvalHistory } from '@/hooks/useAthleteHistory';
import { useAuth } from '@/contexts/AuthContext';
import { formatDate, scoreLabel } from '@/utils/formatters';
import type { AnnotationTemplate } from '@/services/templateApi';
import type { EvaluationSummaryItem, CoachAthleteEvalHistoryRow } from '@/services/evaluationApi';


// ============================================================================
// Stat card
// ============================================================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  accent?: string;
  isLoading?: boolean;
  subtitle?: string;
}

function StatCard({ label, value, icon, accent = 'var(--color-accent-blue)', isLoading, subtitle }: StatCardProps) {
  return (
    <div
      className="rounded-card p-5 border border-border-subtle transition-colors duration-200"
      style={{
        background: 'var(--color-dark-elevated-1)',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = `color-mix(in srgb, ${accent} 45%, var(--color-border-subtle))`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border-subtle)';
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="p-2 rounded-lg"
          style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)`, color: accent }}
        >
          {icon}
        </div>
      </div>
      {isLoading ? (
        <div
          className="h-8 w-16 rounded animate-pulse"
          style={{ background: 'var(--color-border-subtle)' }}
        />
      ) : (
        <p
          className="font-heading font-bold"
          style={{ fontFamily: 'var(--font-editorial)', fontSize: '32px', lineHeight: 1, color: 'var(--color-text-primary)' }}
        >
          {value}
        </p>
      )}
      <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
        {label}
      </p>
      {subtitle && (
        <p className="mt-0.5 text-xs" style={{ color: 'var(--color-text-muted)', opacity: 0.7 }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// Feature 1 — Eval budget meter card
// ============================================================================

interface EvalBudgetCardProps {
  used: number;
  limit: number | null | undefined;
  isLoading?: boolean;
}

function EvalBudgetCard({ used, limit, isLoading }: EvalBudgetCardProps) {
  const accent =
    limit == null || limit === 0
      ? 'var(--color-accent-blue)'
      : used / limit >= 0.95
      ? 'var(--color-accent-red)'
      : used / limit >= 0.8
      ? 'var(--color-accent-gold)'
      : 'var(--color-accent-blue)';

  const fillPct =
    limit != null && limit > 0 ? Math.min(1, used / limit) : 0;

  return (
    <div
      className="rounded-card p-5 border border-border-subtle transition-colors duration-200"
      style={{
        background: 'var(--color-dark-elevated-1)',
        cursor: 'default',
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = `color-mix(in srgb, ${accent} 45%, var(--color-border-subtle))`;
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLDivElement).style.borderColor = 'var(--color-border-subtle)';
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="p-2 rounded-lg"
          style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)`, color: accent }}
        >
          <BoltIcon className="w-5 h-5" />
        </div>
      </div>
      {isLoading ? (
        <div
          className="h-8 w-16 rounded animate-pulse mb-1"
          style={{ background: 'var(--color-border-subtle)' }}
        />
      ) : limit === null || limit === undefined ? (
        <p
          className="font-heading font-bold"
          style={{ fontFamily: 'var(--font-editorial)', fontSize: '24px', lineHeight: 1, color: 'var(--color-text-primary)' }}
        >
          Unlimited
        </p>
      ) : limit === 0 ? (
        <p
          className="font-heading font-bold"
          style={{ fontFamily: 'var(--font-editorial)', fontSize: '16px', lineHeight: 1.2, color: 'var(--color-accent-red)' }}
        >
          No evals available
        </p>
      ) : (
        <>
          <p
            className="font-heading font-bold"
            style={{ fontFamily: 'var(--font-editorial)', fontSize: '28px', lineHeight: 1, color: 'var(--color-text-primary)' }}
          >
            {used}
            <span
              className="text-sm font-normal ml-1"
              style={{ color: 'var(--color-text-muted)' }}
            >
              / {limit}
            </span>
          </p>
          <div
            className="mt-2 rounded-full overflow-hidden"
            style={{
              height: '5px',
              background: 'var(--color-border-subtle)',
            }}
          >
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${fillPct * 100}%`,
                background: accent,
              }}
            />
          </div>
          <p
            className="mt-1 text-xs"
            style={{ color: 'var(--color-text-muted)' }}
          >
            {Math.round(fillPct * 100)}% used this month
          </p>
        </>
      )}
      <p className="mt-1 text-sm" style={{ color: 'var(--color-text-muted)' }}>
        Monthly Evals
      </p>
    </div>
  );
}

// ============================================================================
// Template mini-card
// ============================================================================

interface TemplateCardMiniProps {
  template: AnnotationTemplate;
  evalCount: number;
  avgScore: number | null;
  isSelected: boolean;
  onClick: () => void;
  avgCoachingFaithfulness?: number | null;
}

/**
 * Translate a raw coaching faithfulness score (0-1) to a plain-language label.
 * Thresholds are intentionally lenient: 0.6 is a reasonable floor for "good"
 * based on the Phase D faithfulness-flag cutoff already used in the eval list
 * (coaching_faithfulness_score < 0.6 → ExclamationTriangle icon). The template-
 * level badge fires at < 0.55 (slightly lower because it is an average across
 * multiple evals, so a lower average is a stronger signal). We use the same
 * threshold here for the plain-language label so the two signals are consistent.
 *
 *   ≥ 0.6  → "Good"        (coaching narrative reliably reflects the scored form)
 *   < 0.6  → "Needs review" (narrative may not align with scored form — coach should check)
 */
function faithfulnessLabel(score: number): string {
  // Threshold: 0.6 matches the per-eval ExclamationTriangle gate (Feature 2).
  // Scores ≥ 0.6 are considered good-quality coaching narratives.
  return score >= 0.6 ? 'Good' : 'Needs review';
}

function TemplateCardMini({ template, evalCount, avgScore, isSelected, onClick, avgCoachingFaithfulness }: TemplateCardMiniProps) {
  return (
    <button
      onClick={onClick}
      aria-expanded={isSelected}
      className="text-left rounded-card border transition-colors duration-200 group w-full"
      style={{
        background: isSelected
          ? 'color-mix(in srgb, var(--color-accent-blue) 8%, var(--color-dark-elevated-1))'
          : 'var(--color-dark-elevated-1)',
        padding: '20px',
        borderColor: isSelected ? 'var(--color-accent-blue)' : 'var(--color-border-subtle)',
        cursor: 'pointer',
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          (e.currentTarget as HTMLButtonElement).style.borderColor =
            'color-mix(in srgb, var(--color-accent-blue) 40%, var(--color-border-subtle))';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--color-border-subtle)';
        }
      }}
    >
      <div className="flex items-start justify-between mb-3">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: 'color-mix(in srgb, var(--color-accent-blue) 12%, transparent)', color: 'var(--color-accent-blue)' }}
        >
          <ClipboardDocumentListIcon className="w-4 h-4" />
        </div>
        <div className="flex items-center gap-2">
          {avgScore !== null && <ScoreRing score={avgScore} size={38} />}
          {/* Expand affordance: chevron rotates to indicate expanded/collapsed state */}
          <ChevronDownIcon
            className="w-4 h-4 flex-shrink-0 transition-transform duration-200"
            aria-hidden="true"
            style={{
              color: 'var(--color-text-muted)',
              transform: isSelected ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </div>
      </div>

      <h3
        className="font-heading font-semibold text-sm mb-1 group-hover:text-accent-blue transition-colors"
        style={{ color: 'var(--color-text-primary)' }}
      >
        {template.name}
      </h3>

      {template.movement_type && (
        <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
          {template.movement_type}
        </p>
      )}

      <div className="flex items-center gap-1 mt-auto flex-wrap">
        <span
          className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full"
          style={{
            background: 'color-mix(in srgb, var(--color-accent-blue) 10%, transparent)',
            color: 'var(--color-accent-blue)',
          }}
        >
          <ChartBarIcon className="w-3 h-3" />
          {evalCount} eval{evalCount !== 1 ? 's' : ''}
        </span>
        {/* Feature 6 — Low-faithfulness health badge with plain-language label.
            Threshold < 0.55 fires on templates whose average coaching narrative
            quality is below a reliable level (consistent with Feature 2's < 0.6
            per-eval flag, slightly lower because the average dampens single outliers). */}
        {typeof avgCoachingFaithfulness === 'number' && avgCoachingFaithfulness < 0.55 && (
          <span
            data-testid="faithfulness-quality-badge"
            title={`AI coaching quality: ${faithfulnessLabel(avgCoachingFaithfulness)} — templates with low average faithfulness may have annotation issues affecting LLM narrative quality. (score: ${avgCoachingFaithfulness.toFixed(2)})`}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full cursor-help"
            style={{
              background: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
              color: 'var(--color-accent-gold)',
              border: '1px solid color-mix(in srgb, var(--color-warning) 25%, transparent)',
            }}
          >
            <ExclamationTriangleIcon className="w-3 h-3" />
            AI Quality: {faithfulnessLabel(avgCoachingFaithfulness)}
          </span>
        )}
      </div>
    </button>
  );
}

// ============================================================================
// Empty state
// ============================================================================

function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-card border border-dashed border-border-subtle py-14 px-6 text-center"
      style={{ background: 'color-mix(in srgb, var(--color-dark-elevated-2) 50%, transparent)' }}
    >
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
        style={{ background: 'var(--color-dark-elevated-2)', color: 'var(--color-text-muted)' }}
      >
        {icon}
      </div>
      <p className="font-heading font-semibold text-sm mb-1" style={{ color: 'var(--color-text-primary)' }}>
        {title}
      </p>
      <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)', maxWidth: '280px' }}>
        {description}
      </p>
      {action}
    </div>
  );
}

// ============================================================================
// Main CoachDashboard
// ============================================================================

export default function CoachDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: templates, isLoading: templatesLoading } = useTemplates();
  const { data: evaluations, isLoading: evaluationsLoading } = useMyEvaluations();
  const { data: myChannels } = useMyChannels();
  const ownsChannels = (myChannels?.length ?? 0) > 0;

  // ---- Per-template athlete table state ------------------------------------
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // Last-resort fallback: first channel the coach owns.
  const primaryChannelSlug = myChannels?.[0]?.slug ?? null;

  // Map template ID → its own channel_slug (populated by Phase A backend).
  // Falls back to primaryChannelSlug for templates created before Phase A or
  // without a channel association.
  const templateSlugMap = useMemo(() => {
    const m: Record<string, string | undefined> = {};
    (templates ?? []).forEach((t: AnnotationTemplate) => {
      if (t.channel_slug) m[t.id] = t.channel_slug;
    });
    return m;
  }, [templates]);

  function handleTemplateCardClick(templateId: string) {
    setSelectedTemplateId((prev) => (prev === templateId ? null : templateId));
  }
  const { data: athleteHistory, isLoading: athleteHistoryLoading } = useCoachAthleteEvalHistory(
    { limit: 50, offset: 0 }
  );

  const isLoading = templatesLoading || evaluationsLoading || athleteHistoryLoading;

  // ---- "Active This Week" — athletes with at least one eval in last 7 days --

  const activeThisWeek = useMemo(() => {
    if (!athleteHistory) return 0;
    const sevenDaysAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    return athleteHistory.filter(
      (r: CoachAthleteEvalHistoryRow) => r.last_eval_at && new Date(r.last_eval_at).getTime() >= sevenDaysAgo
    ).length;
  }, [athleteHistory]);

  // ---- derived stats --------------------------------------------------------

  const stats = useMemo(() => {
    const totalTemplates = templates?.length ?? 0;
    const totalEvaluations = evaluations?.length ?? 0;

    const scores = (evaluations ?? [])
      .map((e: EvaluationSummaryItem) => e.overall_score)
      .filter((s): s is number => typeof s === 'number' && !isNaN(s));
    const avgScore = scores.length > 0 ? scores.reduce((a: number, b: number) => a + b, 0) / scores.length : null;

    // Count distinct athletes from the per-athlete roll-up (athleteHistory has one
    // row per athlete user, so its length is the correct "Active Athletes" count).
    // Counting athlete_video_id from evaluations would inflate the figure — one
    // athlete with 10 uploads would count as 10.
    const uniqueAthletes = athleteHistory?.length ?? 0;

    return { totalTemplates, totalEvaluations, avgScore, uniqueAthletes };
  }, [templates, evaluations, athleteHistory]);

  // ---- chart data: last 20 evaluations sorted by date ----------------------

  const chartData = useMemo(() => {
    if (!evaluations?.length) return [];
    const sorted = [...evaluations]
      .filter((e): e is EvaluationSummaryItem & { overall_score: number } =>
        !!e.created_at && typeof e.overall_score === 'number')
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .slice(-20);

    return sorted.map((e) => ({
      date: formatDate(e.created_at, 'MMM d'),
      score: Math.round(e.overall_score),
      athleteName: e.athlete_video_id?.slice(0, 8) ?? '',
    }));
  }, [evaluations]);

  // ---- recent evaluations table (last 10) ----------------------------------

  const recentEvaluations = useMemo(() => {
    if (!evaluations?.length) return [];
    return [...evaluations]
      .filter((e) => e.created_at)
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
      .slice(0, 10);
  }, [evaluations]);

  // ---- per-template evaluation counts and avg scores -----------------------

  const templateStats = useMemo(() => {
    const map: Record<string, { count: number; scores: number[] }> = {};
    (evaluations ?? []).forEach((e: EvaluationSummaryItem) => {
      if (!e.template_id) return;
      if (!map[e.template_id]) map[e.template_id] = { count: 0, scores: [] };
      map[e.template_id].count += 1;
      if (typeof e.overall_score === 'number') {
        map[e.template_id].scores.push(e.overall_score);
      }
    });
    return map;
  }, [evaluations]);

  const displayName = user?.display_name || user?.email?.split('@')[0] || 'Coach';

  // ---- template name lookup for evaluation table ---------------------------
  const templateNameMap = useMemo(() => {
    const m: Record<string, string> = {};
    (templates ?? []).forEach((t: AnnotationTemplate) => { m[t.id] = t.name; });
    return m;
  }, [templates]);

  return (
    <div className="space-y-8 pb-10" style={{ animation: 'fadeInUp 0.4s ease both' }}>

      {/* ------------------------------------------------------------------ */}
      {/* Welcome Banner                                                       */}
      {/* ------------------------------------------------------------------ */}
      <div
        className="rounded-hero p-8 relative overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, var(--color-dark-elevated-1) 0%, var(--color-dark-base) 100%)',
          border: '1px solid var(--color-border-subtle)',
        }}
      >
        {/* decorative glow */}
        <div
          className="absolute -top-12 -right-12 w-48 h-48 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, color-mix(in srgb, var(--color-accent-blue) 12%, transparent) 0%, transparent 70%)',
          }}
        />

        <div className="relative">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <SparklesIcon className="w-4 h-4" style={{ color: 'var(--color-accent-blue)' }} />
                <span className="text-xs font-medium" style={{ color: 'var(--color-accent-blue)' }}>
                  Coach Dashboard
                </span>
              </div>

              <h1
                className="font-heading font-bold mb-1"
                style={{ fontFamily: 'var(--font-editorial)', fontSize: '28px', color: 'var(--color-text-primary)', lineHeight: 1.25 }}
              >
                Welcome back, {displayName}
              </h1>
              <p className="mb-6 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Here's how your athletes are doing
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => navigate('/templates/create')}
              variant="outline"
              size="sm"
              className="border-border-strong"
            >
              <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
              Open Coaching Studio
            </Button>
            {/* B-2026-0020: surface the coach-only "create channel" privilege
                on the dashboard (was buried in the Channels page). */}
            <Button
              onClick={() => navigate('/channels/create')}
              variant="outline"
              size="sm"
              className="border-border-strong"
            >
              <UserGroupIcon className="w-4 h-4 mr-2" />
              Create Channel
            </Button>
            <Button
              onClick={() => navigate('/evaluate')}
              size="sm"
              className="font-semibold"
              style={{
                /* PT-2: no inline color overrides — the Button primary variant
                   is dark-on-accent (cyan). Keep only the token glow. */
                boxShadow: 'var(--shadow-glow-blue)',
              }}
            >
              <PlayIcon className="w-4 h-4 mr-2" />
              Evaluate Athlete
            </Button>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Stats Row                                                            */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <StatCard
          label="Templates"
          value={isLoading ? '-' : stats.totalTemplates}
          icon={<DocumentDuplicateIcon className="w-5 h-5" />}
          accent="var(--color-accent-blue)"
          isLoading={isLoading}
        />
        <StatCard
          label="Evaluations"
          value={isLoading ? '-' : stats.totalEvaluations}
          icon={<ChartBarIcon className="w-5 h-5" />}
          accent="var(--color-accent)"
          isLoading={isLoading}
        />
        <StatCard
          label="Average Score"
          value={
            isLoading
              ? '-'
              : stats.avgScore !== null
              ? `${Math.round(stats.avgScore)}`
              : '—'
          }
          subtitle={!isLoading && stats.avgScore !== null ? scoreLabel(stats.avgScore) : undefined}
          icon={<SparklesIcon className="w-5 h-5" />}
          accent="var(--color-accent-gold)"
          isLoading={isLoading}
        />
        <StatCard
          label="Active Athletes"
          value={isLoading ? '-' : stats.uniqueAthletes}
          icon={<UserGroupIcon className="w-5 h-5" />}
          accent="var(--color-accent-orange)"
          isLoading={isLoading}
        />
        <StatCard
          label="Active This Week"
          value={isLoading ? '-' : activeThisWeek}
          subtitle="athletes evaluating in last 7 days"
          icon={<BoltIcon className="w-5 h-5" />}
          accent="var(--color-accent)"
          isLoading={isLoading}
        />
        {/* Feature 1 — Eval budget meter */}
        <EvalBudgetCard
          used={user?.monthly_eval_used ?? 0}
          limit={user?.monthly_eval_limit}
          isLoading={isLoading}
        />
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Score Trends Chart                                                   */}
      {/* ------------------------------------------------------------------ */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle style={{ fontFamily: 'var(--font-editorial)' }}>Score Trends</CardTitle>
            <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
              Last {Math.min(chartData.length, 20)} evaluations
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {evaluationsLoading ? (
            <div className="flex items-center justify-center h-52">
              <Spinner size="lg" />
            </div>
          ) : chartData.length === 0 ? (
            <EmptyState
              icon={<ChartBarIcon className="w-6 h-6" />}
              title="No evaluation data yet"
              description="Score trends will appear here once you run your first evaluation."
              action={
                <Button size="sm" onClick={() => navigate('/evaluate')}>
                  <PlayIcon className="w-4 h-4 mr-2" />
                  Start Evaluating
                </Button>
              }
            />
          ) : (
            <div style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--color-brand-primary)" stopOpacity={0.25} />
                      <stop offset="95%" stopColor="var(--color-brand-primary)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="var(--color-border-subtle)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fill: 'var(--color-text-muted)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  {/* isAnimationActive=false: recharts otherwise transitions the
                      tooltip from the chart origin (upper-left) to the cursor on
                      first hover — reads as an unnatural fly-in (owner 2026-08-02).
                      Off = it appears at the cursor immediately. */}
                  <Tooltip content={<ChartTooltip />} isAnimationActive={false} />
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke="var(--color-accent-blue)"
                    strokeWidth={2.5}
                    fill="url(#scoreGradient)"
                    dot={false}
                    activeDot={{
                      r: 5,
                      fill: 'var(--color-accent-blue)',
                      stroke: 'var(--color-dark-card)',
                      strokeWidth: 2,
                    }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------------ */}
      {/* Shared Evaluations Inbox (B-2026-0020)                              */}
      {/* ------------------------------------------------------------------ */}
      <SharedEvalsInboxCard />

      {/* ------------------------------------------------------------------ */}
      {/* Two-column: Recent Evaluations + Templates                          */}
      {/* ------------------------------------------------------------------ */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

        {/* Recent Evaluations Table — wider column */}
        <div className="xl:col-span-3">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'var(--font-editorial)' }}>Recent Evaluations</CardTitle>
                {(evaluations?.length ?? 0) > 0 && (
                  <Link
                    to="/evaluations"
                    className="flex items-center gap-1 text-xs font-medium transition-colors hover:text-text-primary"
                    style={{ color: 'var(--color-accent-blue)' }}
                  >
                    View all
                    <ArrowRightIcon className="w-3 h-3" />
                  </Link>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {evaluationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner />
                </div>
              ) : recentEvaluations.length === 0 ? (
                <EmptyState
                  icon={<ChartBarIcon className="w-6 h-6" />}
                  title="No evaluations yet"
                  description="Evaluate an athlete video against a template to see results here."
                  action={
                    <Button size="sm" onClick={() => navigate('/evaluate')}>
                      <PlusIcon className="w-4 h-4 mr-2" />
                      New Evaluation
                    </Button>
                  }
                />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
                        <th
                          className="text-left pb-3 pr-4 font-medium text-xs"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          Athlete
                        </th>
                        <th
                          className="text-left pb-3 pr-4 font-medium text-xs"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          Template
                        </th>
                        <th
                          className="text-center pb-3 pr-4 font-medium text-xs"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          Score
                        </th>
                        <th
                          className="text-center pb-3 pr-4 font-medium text-xs"
                          style={{ color: 'var(--color-text-muted)' }}
                          title="AI Quality: flags evaluations where the AI coaching may not align well with the scored form"
                        >
                          AI Quality
                        </th>
                        <th
                          className="text-left pb-3 font-medium text-xs"
                          style={{ color: 'var(--color-text-muted)' }}
                        >
                          Date
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentEvaluations.map((evaluation, idx: number) => (
                        <tr
                          key={evaluation.evaluation_id}
                          className="transition-colors cursor-pointer group"
                          style={{
                            borderBottom:
                              idx < recentEvaluations.length - 1
                                ? '1px solid var(--color-border-subtle)'
                                : 'none',
                          }}
                          onClick={() => navigate(`/evaluations/${evaluation.evaluation_id}`)}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.background =
                              'color-mix(in srgb, var(--color-accent-blue) 4%, transparent)';
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLTableRowElement).style.background = 'transparent';
                          }}
                        >
                          <td className="py-3 pr-4">
                            <span
                              className="font-medium text-xs"
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              {/* TODO(Tranche-D): show athlete display name once backend joins User for user_id on EvaluationSummaryItem */}
                              {evaluation.user_display_name
                                ?? evaluation.created_by_display_name
                                ?? evaluation.athlete_video_id?.slice(0, 12) ?? '—'}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span
                              className="text-xs"
                              style={{ color: 'var(--color-text-secondary)' }}
                            >
                              {templateNameMap[evaluation.template_id] ?? evaluation.template_id?.slice(0, 10) ?? '—'}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <div className="flex justify-center">
                              {typeof evaluation.overall_score === 'number' ? (
                                <ScoreRing score={evaluation.overall_score} size={36} />
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                                  —
                                </span>
                              )}
                            </div>
                          </td>
                          {/* Feature 2 — Plain-language AI quality badge.
                              faithfulnessLabel() uses 0.6 as the threshold:
                              ≥ 0.6 → "Good" (coaching aligns with scored form)
                              < 0.6 → "Needs review" (narrative may not reflect form accurately) */}
                          <td className="py-3 pr-4">
                            <div className="flex justify-center">
                              {typeof evaluation.coaching_faithfulness_score === 'number' ? (
                                <span
                                  data-testid="faithfulness-quality-badge"
                                  title={`AI coaching quality: ${faithfulnessLabel(evaluation.coaching_faithfulness_score)} (faithfulness score: ${evaluation.coaching_faithfulness_score.toFixed(2)})`}
                                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full cursor-help"
                                  style={
                                    evaluation.coaching_faithfulness_score < 0.6
                                      ? {
                                          background: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
                                          color: 'var(--color-accent-gold)',
                                          border: '1px solid color-mix(in srgb, var(--color-warning) 22%, transparent)',
                                        }
                                      : {
                                          background: 'color-mix(in srgb, var(--color-success) 8%, transparent)',
                                          /* PT-4: match text to the pill's own signal (was azure on a
                                             green pill — a pre-existing mismatch; the warn variant
                                             already uses gold-on-amber). */
                                          color: 'var(--color-success)',
                                          border: '1px solid color-mix(in srgb, var(--color-success) 18%, transparent)',
                                        }
                                  }
                                >
                                  {evaluation.coaching_faithfulness_score < 0.6 && (
                                    <ExclamationTriangleIcon className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                                  )}
                                  {faithfulnessLabel(evaluation.coaching_faithfulness_score)}
                                </span>
                              ) : (
                                <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>—</span>
                              )}
                            </div>
                          </td>
                          <td className="py-3">
                            <span
                              className="text-xs"
                              style={{ color: 'var(--color-text-muted)' }}
                            >
                              {formatDate(evaluation.created_at)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Templates Grid — narrower column */}
        <div className="xl:col-span-2">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle style={{ fontFamily: 'var(--font-editorial)' }}>Templates</CardTitle>
                <Link
                  to="/templates"
                  className="flex items-center gap-1 text-xs font-medium transition-colors hover:text-text-primary"
                  style={{ color: 'var(--color-accent-blue)' }}
                >
                  View all
                  <ArrowRightIcon className="w-3 h-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {templatesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Spinner />
                </div>
              ) : !templates || templates.length === 0 ? (
                <EmptyState
                  icon={<DocumentDuplicateIcon className="w-6 h-6" />}
                  title="No templates yet"
                  description="Create your first movement template to start evaluating athletes."
                  action={
                    <Button size="sm" onClick={() => navigate('/templates/create')}>
                      <PlusIcon className="w-4 h-4 mr-2" />
                      Open Coaching Studio
                    </Button>
                  }
                />
              ) : (
                <div className="space-y-3">
                  {templates.slice(0, 6).map((template) => {
                    const tStats = templateStats[template.id];
                    const evalCount = tStats?.count ?? 0;
                    const avgScore =
                      tStats?.scores?.length
                        ? tStats.scores.reduce((a: number, b: number) => a + b, 0) / tStats.scores.length
                        : null;
                    return (
                      <TemplateCardMini
                        key={template.id}
                        template={template}
                        evalCount={evalCount}
                        avgScore={avgScore}
                        isSelected={selectedTemplateId === template.id}
                        onClick={() => handleTemplateCardClick(template.id)}
                        avgCoachingFaithfulness={template.avg_coaching_faithfulness}
                      />
                    );
                  })}
                  {templates.length > 6 && (
                    <button
                      onClick={() => navigate('/templates')}
                      className="w-full py-2 text-xs font-medium transition-colors rounded-lg"
                      style={{
                        color: 'var(--color-accent-blue)',
                        background: 'color-mix(in srgb, var(--color-accent-blue) 6%, transparent)',
                        border: '1px dashed color-mix(in srgb, var(--color-accent-blue) 30%, transparent)',
                      }}
                    >
                      + {templates.length - 6} more templates
                    </button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ------------------------------------------------------------------ */}
      {/* Template Effectiveness                                               */}
      {/* ------------------------------------------------------------------ */}
      {(templates?.length ?? 0) > 0 && (
        <TemplateEffectivenessCard
          templates={(templates ?? []).map((t) => ({ id: t.id, name: t.name }))}
          evaluations={evaluations ?? []}
        />
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Per-template athlete table (expands when a template card is clicked) */}
      {/* ------------------------------------------------------------------ */}
      {selectedTemplateId && (() => {
        // Resolve the channel slug specific to this template; fall back to
        // the coach's primary channel for legacy/unscoped templates.
        const resolvedChannelSlug =
          templateSlugMap[selectedTemplateId] ?? primaryChannelSlug;

        // Only render the expansion panel when we have a slug to query against.
        if (!resolvedChannelSlug) return null;

        return (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserGroupIcon className="w-5 h-5" style={{ color: 'var(--color-accent-blue)' }} />
                  <CardTitle style={{ fontFamily: 'var(--font-editorial)' }}>
                    {templateNameMap[selectedTemplateId]
                      ? `Athletes — ${templateNameMap[selectedTemplateId]}`
                      : 'Athletes'}
                  </CardTitle>
                </div>
                <button
                  onClick={() => setSelectedTemplateId(null)}
                  aria-label="Close athlete table"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                    color: 'var(--color-text-muted)',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background =
                      'var(--color-dark-elevated-2)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  <XMarkIcon style={{ width: '16px', height: '16px' }} />
                </button>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Side-by-side on desktop (grid 1fr 1fr), stacked on mobile */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  gap: '20px',
                  alignItems: 'start',
                }}
              >
                <PerTemplateAthleteTable
                  channelSlug={resolvedChannelSlug}
                  templateId={selectedTemplateId}
                  templateName={templateNameMap[selectedTemplateId]}
                />
                <CommonWeaknessCard
                  channelSlug={resolvedChannelSlug}
                  templateId={selectedTemplateId}
                />
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* ------------------------------------------------------------------ */}
      {/* My Athletes Roll-up (only when coach owns ≥1 channel)             */}
      {/* ------------------------------------------------------------------ */}
      {ownsChannels && (athleteHistory?.length ?? 0) > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserGroupIcon className="w-5 h-5" style={{ color: 'var(--color-accent-blue)' }} />
                <CardTitle style={{ fontFamily: 'var(--font-editorial)' }}>My Athletes</CardTitle>
              </div>
              <Link
                to="/athletes"
                className="flex items-center gap-1 text-xs font-medium transition-colors hover:text-text-primary"
                style={{ color: 'var(--color-accent-blue)' }}
              >
                View all
                <ArrowRightIcon className="w-3 h-3" />
              </Link>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {athleteHistoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <Spinner />
              </div>
            ) : (
              <AthleteRollupTable rows={athleteHistory ?? []} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Entrance animation keyframe */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
